import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { getStoredToken, getUser } from "../utils/auth";
import dollar from "../assets/images/icons/dollar.svg";
import walletIcon from "../assets/images/icons/greenwallet.png";
import paymentsuccess from "../assets/images/icons/Payment-success.svg";
import paymentfailed from "../assets/images/icons/retry-payment.svg";
import Retryicon from "../assets/images/icons/refresh-right-square.svg";
import PayPalDonateButton from "./PayPalDonateButton";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const PRESET_AMOUNTS = [15, 25, 50, 100];
const MIN_DONATION = 0.5;
const METHOD_LABELS = { card: "Credit/Debit Card", wallet: "Wallet", paypal: "PayPal" };

const formatPrice = (value) => `$${(Number(value) || 0).toFixed(2)}`;
const formatDateTime = (value) => new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

const authHeaders = () => ({ "Content-Type": "application/json", token: getStoredToken() });

const requestJson = async (path, body) => {
  const res = await fetch(`${API_BASE_URL}${path}`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.message || "Something went wrong");
  return data.result;
};

const useWalletBalance = () => {
  const [balance, setBalance] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/wallet/balance`, { headers: authHeaders() });
      const data = await res.json();
      setBalance(data.success ? Number(data.result?.balance) || 0 : null);
    } catch (error) {
      setBalance(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { balance, refresh };
};

/**
 * Donate to a donation post, laid out like the Go Live donate popup: amount -> payment -> result.
 * Money is in $. Card payments go through Stripe (recorded by the webhook, card details are never
 * stored); wallet donations are recorded immediately and credited 100% to the post owner's wallet;
 * PayPal donations are captured by the API right after the donor approves them in PayPal.
 * `onDonated(amount, refreshDelay)` lets the page refresh its totals; `onDone` closes the popup.
 */
const PostDonatePanel = ({ postId, donorId, onDonated, onDone }) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { balance, refresh: refreshBalance } = useWalletBalance();

  const [step, setStep] = useState("amount"); // amount | payment | result
  const [amount, setAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [method, setMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const total = Number(customAmount) || amount || 0;
  const belowMinimum = total > 0 && total < MIN_DONATION;
  const insufficientBalance = method === "wallet" && balance !== null && total > balance;

  const selectAmount = (value) => {
    setAmount(value);
    setCustomAmount("");
  };

  const handleCustomAmount = (e) => {
    const { value } = e.target;
    if (value === "" || /^\d{0,7}(\.\d{0,2})?$/.test(value)) {
      setCustomAmount(value);
      setAmount(null);
    }
  };

  const handlePay = async () => {
    setSubmitting(true);
    const receipt = { total, date: new Date() };
    try {
      if (method === "wallet") {
        const data = await requestJson("/donator/wallet-donation", { post_id: Number(postId), amount: total });
        Object.assign(receipt, { reference: data.donation.id, method: "Wallet" });
        refreshBalance();
        onDonated?.(total, 0);
      } else {
        if (!stripe || !elements) throw new Error("Card payments are not available right now");
        const card = elements.getElement(CardElement);
        const data = await requestJson("/donator/create-payment", {
          amount: total,
          currency: "usd",
          metadata: { user_id: donorId, post_id: postId },
        });
        const { error, paymentIntent } = await stripe.confirmCardPayment(data.client_secret, {
          payment_method: { card },
        });
        if (error) throw new Error(error.message);
        if (paymentIntent.status !== "succeeded") throw new Error("The payment was not completed");
        Object.assign(receipt, { reference: paymentIntent.id, method: "Credit/Debit Card" });
        // Card donations are recorded by the Stripe webhook, so the page refreshes a little later
        onDonated?.(total, 2000);
      }
      setResult({ ...receipt, success: true });
    } catch (error) {
      if (method === "wallet") refreshBalance();
      setResult({
        ...receipt,
        success: false,
        error: error.message,
        method: METHOD_LABELS[method],
      });
    } finally {
      setSubmitting(false);
      setStep("result");
    }
  };

  const finishPayPal = (outcome) => {
    setResult({ total, date: new Date(), method: METHOD_LABELS.paypal, ...outcome });
    setStep("result");
  };
  const handlePayPalSuccess = (donation) => {
    onDonated?.(total, 0);
    finishPayPal({ success: true, reference: donation?.capture_id || donation?.id });
  };
  const handlePayPalPending = (message) => finishPayPal({ success: false, pending: true, error: message });
  const handlePayPalError = (message) => finishPayPal({ success: false, error: message });

  const senderName = (() => {
    const user = getUser();
    return [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.username || "-";
  })();

  if (step === "result" && result) {
    return (
      <div className="flex flex-col items-center px-4 py-6 text-center">
        <img src={result.success ? paymentsuccess : paymentfailed} alt="" className="h-28 w-28" />
        <h3 className={`mt-2 text-lg font-semibold ${result.success ? "text-black" : "text-[#BE1F2E]"}`}>
          {result.success ? "Thank You for Supporting!" : result.pending ? "Payment Under Review" : "Payment Failed"}
        </h3>
        <p className="text-sm text-gray-600">
          {result.success ? "Your donation has been sent successfully." : result.error || "Unfortunately, we couldn't process your payment."}
        </p>
        <p className="mt-4 text-sm text-gray-600">Total Payment</p>
        <p className="text-4xl font-semibold">{formatPrice(result.total)}</p>
        <p className="text-xs font-medium">{formatDateTime(result.date)}</p>

        <div className="mt-6 grid w-full max-w-2xl grid-cols-1 gap-3 text-left sm:grid-cols-3">
          {[
            ["Payment Ref No.", result.success ? result.reference : result.pending ? "Pending" : "Failed"],
            ["Sender Name", senderName],
            ["Payment Method", result.method],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-[#EDEDED] p-3">
              <p className="text-[11px] text-gray-500">{label}</p>
              <p className={`break-all text-sm ${label === "Payment Ref No." && !result.success ? "text-[#BE1F2E]" : ""}`}>
                {value}
              </p>
            </div>
          ))}
        </div>

        {result.success ? (
          <button onClick={onDone} className="mt-8 rounded-full bg-[#095228] px-16 py-3 font-semibold text-white">
            Done
          </button>
        ) : (
          <button
            onClick={() => setStep("payment")}
            className="mt-8 flex items-center gap-2 rounded-full bg-[#BE1F2E] px-8 py-2 font-semibold text-white"
          >
            <img src={Retryicon} alt="" className="h-5 w-5" />
            Retry Payment
          </button>
        )}
      </div>
    );
  }

  const amountStep = (
    <div className="flex h-full flex-col rounded-2xl bg-white p-4">
      <p className="mb-3 font-semibold">Choose Your Amount</p>
      <div className="flex flex-wrap items-center gap-3">
        {PRESET_AMOUNTS.map((value) => (
          <button
            key={value}
            onClick={() => selectAmount(value)}
            className={`flex items-center gap-2 rounded-full border px-3 py-1 font-medium transition ${
              amount === value ? "border-[#FF7A51] bg-[#FFB800]" : "border-[#FFE7A3] bg-[#FFE7A3] opacity-70 hover:opacity-100"
            }`}
          >
            <img src={dollar} alt="" className="h-4 w-4" />
            {value}
          </button>
        ))}
        <div
          className={`flex h-11 items-center gap-2 rounded-xl border px-3 ${
            customAmount ? "border-[#095228] bg-[#F3F8F5]" : "border-gray-300"
          }`}
        >
          <span className="text-gray-500">$</span>
          <input
            inputMode="decimal"
            value={customAmount}
            onChange={handleCustomAmount}
            placeholder="0"
            className="w-24 bg-transparent text-right outline-none"
          />
        </div>
      </div>
      {belowMinimum && (
        <p className="mt-2 text-xs text-[#BE1F2E]">The minimum donation is {formatPrice(MIN_DONATION)}.</p>
      )}
    </div>
  );

  const paymentStep = (
    <div className="space-y-4">
      <label className="block cursor-pointer rounded-2xl bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <input type="radio" checked={method === "card"} onChange={() => setMethod("card")} className="accent-[#095228]" />
          <span className="text-sm font-semibold">Pay with Card</span>
        </div>
        {method === "card" && (
          <div className="rounded-xl border border-[#095228] bg-white px-4 py-3">
            <CardElement options={{ hidePostalCode: true, style: { base: { fontSize: "15px" } } }} />
          </div>
        )}
      </label>

      <label className="block cursor-pointer rounded-2xl bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <input type="radio" checked={method === "wallet"} onChange={() => setMethod("wallet")} className="accent-[#095228]" />
          <span className="text-sm font-semibold">Wallet</span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 rounded-xl bg-[#F4F4F4] px-4 py-3">
            <img src={walletIcon} alt="" className="h-10 w-10" />
            <span className="text-2xl font-semibold">{balance === null ? "--" : formatPrice(balance)}</span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/wallet")}
            className="rounded-full bg-[#095228] px-8 py-2 text-sm text-white"
          >
            Top Up Wallet
          </button>
        </div>
        {insufficientBalance && (
          <p className="mt-2 text-xs text-[#BE1F2E]">Your wallet balance is too low. Top up or pay with a card.</p>
        )}
      </label>

      <label className="block cursor-pointer rounded-2xl bg-white p-4">
        <div className="flex items-center gap-2">
          <input type="radio" checked={method === "paypal"} onChange={() => setMethod("paypal")} className="accent-[#095228]" />
          <span className="text-sm font-semibold">PayPal</span>
        </div>
        {method === "paypal" && (
          <p className="mt-2 text-xs text-gray-500">
            Use the PayPal button to log in to PayPal and approve the payment.
          </p>
        )}
      </label>
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
      <div className="rounded-2xl bg-[#F4F4F4] p-3">{step === "amount" ? amountStep : paymentStep}</div>

      <div className="h-fit rounded-2xl border p-5">
        <h3 className="mb-4 text-lg font-semibold">Order Summary</h3>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Subtotal</span>
          <span className="font-semibold">{formatPrice(total)}</span>
        </div>
        <hr className="my-3" />
        <div className="flex justify-between text-lg font-semibold">
          <span>Total</span>
          <span>{formatPrice(total)}</span>
        </div>
        {step === "amount" ? (
          <button
            onClick={() => setStep("payment")}
            disabled={!(total >= MIN_DONATION)}
            className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#095228] text-white disabled:opacity-50"
          >
            Donate <span>→</span>
          </button>
        ) : (
          <>
            {method === "paypal" ? (
              <div className="mt-5">
                <PayPalDonateButton
                  postId={postId}
                  amount={total}
                  disabled={submitting}
                  onBusy={setSubmitting}
                  onSuccess={handlePayPalSuccess}
                  onPending={handlePayPalPending}
                  onError={handlePayPalError}
                />
              </div>
            ) : (
            <button
              onClick={handlePay}
              disabled={submitting || insufficientBalance || (method === "wallet" && balance === null)}
              className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#095228] text-white disabled:opacity-50"
            >
              {submitting ? "Processing..." : <>Pay Now <span>→</span></>}
            </button>
            )}
            <button
              onClick={() => setStep("amount")}
              disabled={submitting}
              className="mt-3 w-full text-sm text-gray-500 hover:underline"
            >
              Go Back
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PostDonatePanel;
